import React, { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export default function SubmitForm({ type, showName = false, showEmail = true, emailRequired = true,
  subjectLabel, messageLabel, subjectPlaceholder = "", messagePlaceholder = "", buttonLabel }) {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error(t("Preenche o assunto e a descrição.", "Fill the subject and description."));
      return;
    }
    if (showEmail && emailRequired && !email.trim()) {
      toast.error(t("Indica o teu email.", "Enter your email."));
      return;
    }
    setSending(true);
    try {
      await api.post("/public/submissions", { type, name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid={`submit-success-${type}`} className="not-prose rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-foreground">{t("Recebido. Obrigado!", "Received. Thank you!")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("Vamos analisar a tua mensagem em breve.", "We'll review your message soon.")}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`submit-form-${type}`} className="not-prose space-y-4 rounded-2xl border border-white/10 bg-card p-6">
      {showName && (
        <div>
          <Label className="text-xs">{t("Nome (opcional)", "Name (optional)")}</Label>
          <Input data-testid="form-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder={t("O teu nome", "Your name")} />
        </div>
      )}
      {showEmail && (
        <div>
          <Label className="text-xs">{t("Email", "Email")}{emailRequired && " *"}</Label>
          <Input data-testid="form-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="voce@email.com" />
        </div>
      )}
      <div>
        <Label className="text-xs">{subjectLabel} *</Label>
        <Input data-testid="form-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" placeholder={subjectPlaceholder} />
      </div>
      <div>
        <Label className="text-xs">{messageLabel} *</Label>
        <Textarea data-testid="form-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="mt-1.5" placeholder={messagePlaceholder} />
      </div>
      <Button data-testid={`form-submit-${type}`} type="submit" disabled={sending} className="gap-2">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {buttonLabel || t("Enviar", "Send")}
      </Button>
    </form>
  );
}
